import React from 'react';
import { Field, FieldError, FieldInfo } from '..';
import { useForm } from 'react-final-form';
import PopupLink from '../../../02_molecules/PopupLink';
import programmeStyles from '../../../03_organisms/BodyBlocks/BBProgramme/index.module.scss';
import FormattedText from '../../FormattedText';

type Props = {
  name: string;
  label: string;
  popupTitle?: string;
  popupHtmlContent: string;
  onKeyDown?: (e: React.KeyboardEvent, value: string | boolean, setValue: () => void) => void;
  info?: string;
  isRequired?: boolean;
};

type CheckboxOptionValue = string | boolean;

const CheckboxFieldWithPopup = ({
  name,
  label,
  popupTitle = '',
  popupHtmlContent,
  onKeyDown,
  info = '',
  isRequired = false,
  ...props
}: Props) => {
  const { mutators } = useForm();
  const labelParts = label.split(/({.+?})/g).map((text, index) => {
    if (text.length && text.startsWith('{')) {
      // This is popup link.
      const linkText = text.replace(/[{}]/g, '');
      return (
        <PopupLink key={index} linkText={linkText} className={programmeStyles['popup-programme']}>
          <>
            {popupTitle.length && <h2>{popupTitle}</h2>}
            <FormattedText text={popupHtmlContent} />
          </>
        </PopupLink>
      );
    }

    // This is common part of label.
    return text;
  });

  const handleKeyDown = (e: React.KeyboardEvent, value: CheckboxOptionValue) => {
    if (onKeyDown) {
      onKeyDown(e, value, mutators.setValue);
      return;
    }

    const enter = 13;
    const space = 32;
    if ((e.keyCode === enter || e.keyCode === space) && mutators.setValue) {
      e.preventDefault();
      mutators.setValue(name, !value);
    }
  };

  return (
    <Field name={name} subscription={{ error: true, touched: true }}>
      {({ meta: { error, touched } }) => (
        <div className={`field-type-checkboxes field-${name} ${error && touched ? 'error' : ''}`}>
          <Field
            name={name}
            key={name}
            render={({ input }) => (
              <div className={`checkbox-item${input.value ? ' selected' : ''}`}>
                <label
                  htmlFor={name}
                  data-value={input.value}
                  tabIndex={0}
                  onKeyDown={(e) => handleKeyDown(e, input.value)}
                >
                  <Field component="input" type="checkbox" name={name} id={name} {...props} />

                  <span className="indicator" />

                  <div className="label">{labelParts}</div>
                </label>
              </div>
            )}
          />

          <FieldInfo name={name} info={info} />
          <FieldError name={name} />
        </div>
      )}
    </Field>
  );
};

export default CheckboxFieldWithPopup;
